import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  colors,
  Tooltip,
  Typography,
} from "@mui/material";
import { CorporateService } from "../models/Service";
import { useNavigate } from "react-router-dom";
import { DEFAULT_SERVICE_LOGO, swapToDefaultServiceLogo } from "../constants";

// The structural minimum this card renders, rather than the whole service. A
// CorporateService satisfies it unchanged, and so does a catalogue row, which
// carries only what a card needs and would otherwise have to be cast.
export type ServiceCardService = Pick<CorporateService, "id" | "service_name"> & {
  // Optional because the catalogue contract marks it so; the tenant service
  // type still satisfies this by carrying it.
  service_description?: string;
  logo?: { url: string };
};

const ServiceCard = ({
  service,
  subsidiaryName,
  onSelect,
}: {
  service: ServiceCardService;
  // Rendered only at platform scope, where a payer sees every school at once
  // and cannot otherwise tell whose service this is.
  subsidiaryName?: string;
  // The platform front door cannot use the relative navigation below: from `/`
  // it would resolve to `/:id`, which the greedy tenant route reads as a school
  // name. Supplying this lets the caller resolve the owning school first.
  onSelect?: (service: ServiceCardService) => void;
}) => {
  const navigate = useNavigate();
  const onSelectService = () => {
    if (onSelect) {
      onSelect(service);
      return;
    }
    navigate(`${service.id}`);
  };

  const logoUrl = service.logo?.url || DEFAULT_SERVICE_LOGO;
  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mx: 2,
          marginBottom: 4,
        }}
        data-testid="service-card"
        onClick={onSelectService}
      >
        <Tooltip title={service?.service_name}>
          <Card
            key={service?.service_name}
            sx={{
              // The school line is a third row. Every card on the platform
              // front door carries it, so the grid stays uniform; the tenant
              // grid never passes it and keeps its original height.
              height: subsidiaryName ? 105 : 85,
              width: 260,
              backgroundColor: "#f7fafe",
              boxShadow: "0px 4px 16px 0px #0000001A",
            }}
            elevation={4}
          >
            <CardActionArea
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                px: 1,
                height: "100%",
              }}
            >
              <img
                style={{ width: 62 }}
                src={logoUrl}
                alt={service.service_name}
                // Defence in depth behind the API boundary: that covers a
                // service with no logo, this covers one whose logo does not load.
                onError={swapToDefaultServiceLogo}
              />

              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: 170,
                  px: 1,
                }}
              >
                <Typography
                  noWrap
                  variant={"body2"}
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: "bolder",
                    fontSize: 12,
                    width: "100%",
                  }}
                >
                  {service.service_name}
                </Typography>

                <Typography
                  noWrap
                  variant={"body2"}
                  sx={{ color: colors.grey[600], width: "100%" }}
                >
                  {service?.service_description}
                </Typography>

                {subsidiaryName ? (
                  <Typography
                    noWrap
                    variant={"body2"}
                    data-testid="service-card-subsidiary"
                    sx={{
                      color: colors.grey[800],
                      width: "100%",
                      fontSize: 11,
                      fontStyle: "italic",
                    }}
                  >
                    {subsidiaryName}
                  </Typography>
                ) : null}
              </CardContent>
            </CardActionArea>
          </Card>
        </Tooltip>
      </Box>
    </>
  );
};

export default ServiceCard;
